import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  X, Settings, Building2, Users, Target, Globe, Briefcase, 
  ChevronDown, Check, Loader2, Save, Edit3, RefreshCw
} from "lucide-react";

// LinkedIn-standard Industry options
const INDUSTRIES = [
  "Accounting", "Airlines/Aviation", "Alternative Dispute Resolution", "Alternative Medicine",
  "Animation", "Apparel & Fashion", "Architecture & Planning", "Arts and Crafts",
  "Automotive", "Aviation & Aerospace", "Banking", "Biotechnology", "Broadcast Media",
  "Building Materials", "Business Supplies and Equipment", "Capital Markets",
  "Chemicals", "Civic & Social Organization", "Civil Engineering", "Commercial Real Estate",
  "Computer & Network Security", "Computer Games", "Computer Hardware", "Computer Networking",
  "Computer Software", "Construction", "Consumer Electronics", "Consumer Goods", "Consumer Services",
  "Cosmetics", "Dairy", "Defense & Space", "Design", "E-Learning", "Education Management",
  "Electrical/Electronic Manufacturing", "Entertainment", "Environmental Services",
  "Events Services", "Executive Office", "Facilities Services", "Farming", "Financial Services",
  "Fine Art", "Fishery", "Food & Beverages", "Food Production", "Fund-Raising", "Furniture",
  "Gambling & Casinos", "Glass, Ceramics & Concrete", "Government Administration",
  "Government Relations", "Graphic Design", "Health, Wellness and Fitness", "Higher Education",
  "Hospital & Health Care", "Hospitality", "Human Resources", "Import and Export",
  "Individual & Family Services", "Industrial Automation", "Information Services",
  "Information Technology and Services", "Insurance", "International Affairs",
  "International Trade and Development", "Internet", "Investment Banking", "Investment Management",
  "Judiciary", "Law Enforcement", "Law Practice", "Legal Services", "Legislative Office",
  "Leisure, Travel & Tourism", "Libraries", "Logistics and Supply Chain", "Luxury Goods & Jewelry",
  "Machinery", "Management Consulting", "Maritime", "Market Research", "Marketing and Advertising",
  "Mechanical or Industrial Engineering", "Media Production", "Medical Devices", "Medical Practice",
  "Mental Health Care", "Military", "Mining & Metals", "Motion Pictures and Film",
  "Museums and Institutions", "Music", "Nanotechnology", "Newspapers", "Non-Profit Organization Management",
  "Oil & Energy", "Online Media", "Outsourcing/Offshoring", "Package/Freight Delivery",
  "Packaging and Containers", "Paper & Forest Products", "Performing Arts", "Pharmaceuticals",
  "Philanthropy", "Photography", "Plastics", "Political Organization", "Primary/Secondary Education",
  "Printing", "Professional Training & Coaching", "Program Development", "Public Policy",
  "Public Relations and Communications", "Public Safety", "Publishing", "Railroad Manufacture",
  "Ranching", "Real Estate", "Recreational Facilities and Services", "Religious Institutions",
  "Renewables & Environment", "Research", "Restaurants", "Retail", "Security and Investigations",
  "Semiconductors", "Shipbuilding", "Sporting Goods", "Sports", "Staffing and Recruiting",
  "Supermarkets", "Telecommunications", "Textiles", "Think Tanks", "Tobacco", "Translation and Localization",
  "Transportation/Trucking/Railroad", "Utilities", "Venture Capital & Private Equity",
  "Veterinary", "Warehousing", "Wholesale", "Wine and Spirits", "Wireless", "Writing and Editing"
];

// LinkedIn-standard Company Size options
const COMPANY_SIZES = [
  { value: "1-10", label: "1-10 employees (Startup)" },
  { value: "11-50", label: "11-50 employees (Small)" },
  { value: "51-200", label: "51-200 employees (Medium)" },
  { value: "201-500", label: "201-500 employees (Mid-Market)" },
  { value: "501-1000", label: "501-1,000 employees (Mid-Enterprise)" },
  { value: "1001-5000", label: "1,001-5,000 employees (Enterprise)" },
  { value: "5001-10000", label: "5,001-10,000 employees (Large Enterprise)" },
  { value: "10001+", label: "10,001+ employees (Global Enterprise)" }
];

// LinkedIn-standard Seniority Levels
const SENIORITY_LEVELS = [
  { value: "entry", label: "Entry Level" },
  { value: "senior", label: "Senior" },
  { value: "manager", label: "Manager" },
  { value: "director", label: "Director" },
  { value: "vp", label: "VP / Vice President" },
  { value: "cxo", label: "C-Suite / Executive" },
  { value: "partner", label: "Partner" },
  { value: "owner", label: "Owner / Founder" }
];

// LinkedIn-standard Job Functions
const JOB_FUNCTIONS = [
  "Accounting", "Administrative", "Arts and Design", "Business Development",
  "Community & Social Services", "Consulting", "Education", "Engineering",
  "Entrepreneurship", "Finance", "Healthcare Services", "Human Resources",
  "Information Technology", "Legal", "Marketing", "Media & Communications",
  "Military & Protective Services", "Operations", "Product Management",
  "Program & Project Management", "Purchasing", "Quality Assurance",
  "Real Estate", "Research", "Sales", "Support"
];

// Geographic Regions
const REGIONS = [
  { value: "north_america", label: "North America" },
  { value: "south_america", label: "South America" },
  { value: "europe", label: "Europe" },
  { value: "asia_pacific", label: "Asia Pacific" },
  { value: "middle_east", label: "Middle East" },
  { value: "africa", label: "Africa" },
  { value: "global", label: "Global / All Regions" }
];

const MultiSelect = ({ options, selected, onChange, placeholder, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };
  
  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-300 mb-2">{label}</label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-white/5 border border-gray-700 rounded-lg px-4 py-3 text-left text-white flex items-center justify-between hover:bg-white/10 transition-colors"
      >
        <span className={selected.length ? "text-white" : "text-gray-500"}>
          {selected.length ? `${selected.length} selected` : placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-[#1a1f24] border border-gray-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {options.map((opt) => {
            const value = typeof opt === 'string' ? opt : opt.value;
            const label = typeof opt === 'string' ? opt : opt.label;
            const isSelected = selected.includes(value);
            
            return (
              <button
                key={value}
                type="button"
                onClick={() => toggleOption(value)}
                className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-white/10 ${isSelected ? 'bg-[#3dd9c5]/10 text-[#3dd9c5]' : 'text-gray-300'}`}
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center ${isSelected ? 'bg-[#3dd9c5] border-[#3dd9c5]' : 'border-gray-600'}`}>
                  {isSelected && <Check className="h-3 w-3 text-black" />}
                </div>
                {label}
              </button>
            );
          })}
        </div>
      )}
      
      {/* Selected tags */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selected.slice(0, 3).map(value => {
            const opt = options.find(o => (typeof o === 'string' ? o : o.value) === value);
            const label = typeof opt === 'string' ? opt : opt?.label || value;
            return (
              <span key={value} className="bg-[#3dd9c5]/20 text-[#3dd9c5] text-xs px-2 py-1 rounded-full">
                {label}
              </span>
            );
          })}
          {selected.length > 3 && (
            <span className="text-gray-500 text-xs py-1">+{selected.length - 3} more</span>
          )}
        </div>
      )}
    </div>
  );
};

const SettingsModal = ({ isOpen, onClose, userId, initialData }) => {
  const [activeTab, setActiveTab] = useState("icp");
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // ICP Settings
  const [icpData, setIcpData] = useState({
    icpDescription: "",
    industries: [],
    companySizes: [],
    companyTypes: [], // public, private, nonprofit
    regions: []
  });
  
  // Persona Settings
  const [personaData, setPersonaData] = useState({
    titles: "",
    functions: [],
    seniorities: [],
    keywords: ""
  });
  
  // Load existing data
  useEffect(() => {
    if (initialData) {
      setIcpData({
        icpDescription: initialData.icp || "",
        industries: initialData.targetIndustries || [],
        companySizes: initialData.targetCompanySizes || [],
        companyTypes: initialData.targetCompanyTypes || [],
        regions: initialData.targetRegions || []
      });
      setPersonaData({
        titles: initialData.targetTitles || "",
        functions: initialData.targetFunctions || [],
        seniorities: initialData.targetSeniorities || [],
        keywords: initialData.personaKeywords || ""
      });
    }
  }, [initialData]);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      // Update user profile with new targeting settings
      const updateData = {
        icp: icpData.icpDescription,
        targetIndustries: icpData.industries,
        targetCompanySizes: icpData.companySizes,
        targetCompanyTypes: icpData.companyTypes,
        targetRegions: icpData.regions,
        targetTitles: personaData.titles,
        targetFunctions: personaData.functions,
        targetSeniorities: personaData.seniorities,
        personaKeywords: personaData.keywords
      };
      
      await axios.put(`${API}/users/${userId}`, updateData);
      toast.success("Settings saved successfully!");
      setHasChanges(false);
      onClose();
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };
  
  const handleIcpChange = (field, value) => {
    setIcpData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };
  
  const handlePersonaChange = (field, value) => {
    setPersonaData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-[#0f1419] to-[#1a1f24] rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-gray-800 shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#e91e8c]/20 to-[#3dd9c5]/20 border-b border-gray-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#e91e8c] to-[#3dd9c5] flex items-center justify-center">
                <Settings className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Targeting Settings</h2>
                <p className="text-gray-400 text-sm">Refine your ICP and persona targeting</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="border-b border-gray-800 px-6">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab("icp")}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "icp"
                  ? "border-[#3dd9c5] text-[#3dd9c5]"
                  : "border-transparent text-gray-400 hover:text-white"
              }`}
            >
              <Building2 className="h-4 w-4 inline mr-2" />
              Company Targeting (ICP)
            </button>
            <button
              onClick={() => setActiveTab("persona")}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "persona"
                  ? "border-[#3dd9c5] text-[#3dd9c5]"
                  : "border-transparent text-gray-400 hover:text-white"
              }`}
            >
              <Users className="h-4 w-4 inline mr-2" />
              Persona Targeting
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === "icp" && (
            <div className="space-y-6">
              {/* ICP Description */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Target className="h-4 w-4 inline mr-2" />
                  Ideal Customer Profile (ICP) Description
                </label>
                <Textarea
                  value={icpData.icpDescription}
                  onChange={(e) => handleIcpChange("icpDescription", e.target.value)}
                  placeholder="Describe your ideal customer... e.g., 'Mid-sized B2B SaaS companies in healthcare and fintech, 50-500 employees, seeking digital transformation solutions'"
                  className="bg-white/5 border-gray-700 text-white min-h-[100px]"
                />
                <p className="text-gray-500 text-xs mt-1">This description helps the AI understand your target market</p>
              </div>
              
              {/* Industries */}
              <MultiSelect
                options={INDUSTRIES}
                selected={icpData.industries}
                onChange={(v) => handleIcpChange("industries", v)}
                placeholder="Select target industries..."
                label={<><Briefcase className="h-4 w-4 inline mr-2" />Target Industries</>}
              />
              
              {/* Company Size */}
              <MultiSelect
                options={COMPANY_SIZES}
                selected={icpData.companySizes}
                onChange={(v) => handleIcpChange("companySizes", v)}
                placeholder="Select company sizes..."
                label={<><Building2 className="h-4 w-4 inline mr-2" />Company Size</>}
              />
              
              {/* Company Type */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Company Type
                </label>
                <div className="flex flex-wrap gap-3">
                  {[
                    { value: "public", label: "Public Companies" },
                    { value: "private", label: "Private Companies" },
                    { value: "nonprofit", label: "Non-Profit" },
                    { value: "government", label: "Government" }
                  ].map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => {
                        const current = icpData.companyTypes;
                        if (current.includes(type.value)) {
                          handleIcpChange("companyTypes", current.filter(v => v !== type.value));
                        } else {
                          handleIcpChange("companyTypes", [...current, type.value]);
                        }
                      }}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                        icpData.companyTypes.includes(type.value)
                          ? "bg-[#3dd9c5]/20 border-[#3dd9c5] text-[#3dd9c5]"
                          : "bg-white/5 border-gray-700 text-gray-400 hover:border-gray-500"
                      }`}
                    >
                      {icpData.companyTypes.includes(type.value) && <Check className="h-3 w-3 inline mr-1" />}
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Geographic Regions */}
              <MultiSelect
                options={REGIONS}
                selected={icpData.regions}
                onChange={(v) => handleIcpChange("regions", v)}
                placeholder="Select target regions..."
                label={<><Globe className="h-4 w-4 inline mr-2" />Geographic Regions</>}
              />
            </div>
          )}
          
          {activeTab === "persona" && (
            <div className="space-y-6">
              {/* Job Titles */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Users className="h-4 w-4 inline mr-2" />
                  Target Job Titles
                </label>
                <Textarea
                  value={personaData.titles}
                  onChange={(e) => handlePersonaChange("titles", e.target.value)}
                  placeholder="Enter target titles, one per line or comma-separated... e.g., CEO, CTO, VP of Sales, Director of Marketing, Head of Operations"
                  className="bg-white/5 border-gray-700 text-white min-h-[80px]"
                />
                <p className="text-gray-500 text-xs mt-1">Common titles you want to target</p>
              </div>
              
              {/* Job Functions */}
              <MultiSelect
                options={JOB_FUNCTIONS}
                selected={personaData.functions}
                onChange={(v) => handlePersonaChange("functions", v)}
                placeholder="Select job functions..."
                label={<><Briefcase className="h-4 w-4 inline mr-2" />Job Functions</>}
              />
              
              {/* Seniority Levels */}
              <MultiSelect
                options={SENIORITY_LEVELS}
                selected={personaData.seniorities}
                onChange={(v) => handlePersonaChange("seniorities", v)}
                placeholder="Select seniority levels..."
                label={<><Target className="h-4 w-4 inline mr-2" />Seniority Level</>}
              />
              
              {/* Keywords */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Edit3 className="h-4 w-4 inline mr-2" />
                  Additional Keywords
                </label>
                <Input
                  value={personaData.keywords}
                  onChange={(e) => handlePersonaChange("keywords", e.target.value)}
                  placeholder="e.g., decision maker, budget holder, digital transformation..."
                  className="bg-white/5 border-gray-700 text-white"
                />
                <p className="text-gray-500 text-xs mt-1">Keywords to include in prospect searches</p>
              </div>
              
              {/* Preview */}
              <div className="bg-white/5 rounded-lg p-4 border border-gray-700">
                <h4 className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 text-[#3dd9c5]" />
                  Targeting Preview
                </h4>
                <p className="text-gray-400 text-sm">
                  Looking for: <span className="text-[#3dd9c5]">{personaData.seniorities.map(s => SENIORITY_LEVELS.find(l => l.value === s)?.label).filter(Boolean).join(", ") || "Any seniority"}</span>
                  {" "}in <span className="text-[#3dd9c5]">{personaData.functions.length ? personaData.functions.slice(0, 3).join(", ") : "any function"}</span>
                  {personaData.titles && <> with titles like <span className="text-[#3dd9c5]">{personaData.titles.split(/[,\n]/).slice(0, 2).join(", ")}</span></>}
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="border-t border-gray-800 px-6 py-4 flex items-center justify-between bg-black/20">
          <div>
            {hasChanges && (
              <span className="text-orange-400 text-sm flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
                Unsaved changes
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="border-gray-700 text-gray-300 hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="bg-[#e91e8c] hover:bg-[#d01a7d] text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
